import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge, Button, Spinner, Table } from 'react-bootstrap';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import Paginacao from '../../components/Paginacao';
import BuscaAvancada from '../../components/BuscaAvancada';

const CAMPOS_BUSCA = [
  { nome: 'nome', rotulo: 'Nome', tipo: 'text' },
  { nome: 'email', rotulo: 'E-mail', tipo: 'text' },
  { nome: 'telefone', rotulo: 'Telefone', tipo: 'text' },
  { nome: 'cpfcnpj', rotulo: 'CPF/CNPJ', tipo: 'text' },
  { nome: 'categoria', rotulo: 'Categoria', tipo: 'text' },
  { nome: 'cidade', rotulo: 'Cidade', tipo: 'text' },
  { nome: 'estado', rotulo: 'Estado (UF)', tipo: 'text' },
  { nome: 'bairro', rotulo: 'Bairro', tipo: 'text' },
  { nome: 'captador', rotulo: 'Captador', tipo: 'text' },
  {
    nome: 'situacao',
    rotulo: 'Situação',
    tipo: 'select',
    opcoes: [
      { valor: 'ativos', rotulo: 'Ativos' },
      { valor: 'bloqueados', rotulo: 'Bloqueados' },
    ],
  },
  { nome: 'criado_de', rotulo: 'Cadastrado a partir de', tipo: 'date' },
  { nome: 'criado_ate', rotulo: 'Cadastrado até', tipo: 'date' },
];

export default function ListaClientes() {
  const toast = useToast();
  const { usuario } = useAuth();
  // `?q=` na URL (link "ver todos" da busca geral do Dashboard) já chega filtrado — só lido na
  // primeira renderização, de propósito (useState, não useEffect): depois disso a busca normal
  // da tela manda.
  const [searchParams] = useSearchParams();
  const [qInicial] = useState(() => searchParams.get('q') || '');
  const [empresas, setEmpresas] = useState([]);
  const [filtros, setFiltros] = useState(() => (qInicial ? { q: qInicial } : {}));
  const [clientes, setClientes] = useState(null);
  const [meta, setMeta] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [erro, setErro] = useState(false);

  // nivel 1 (superadmin) enxerga todas as empresas — ganha um filtro por empresa
  useEffect(() => {
    if (usuario.nivel === 1) api.get('/empresas').then((r) => setEmpresas(r.data)).catch(() => {});
  }, [usuario.nivel]);

  useEffect(() => {
    let cancelado = false;
    setErro(false);
    // filtros da busca (simples `q` + avançados) viajam como query string — o backend valida/limita
    const query = new URLSearchParams({ page: pagina, per_page: perPage, ...filtros });
    api
      .get(`/clientes?${query}`)
      .then((r) => {
        if (cancelado) return;
        setClientes(r.data);
        setMeta(r.meta);
      })
      .catch((err) => {
        if (!cancelado) {
          setErro(true);
          toast.erro(err.message || 'Não foi possível carregar os clientes');
        }
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, perPage, filtros]);

  function aoBuscar(novosFiltros) {
    setFiltros(novosFiltros);
    setPagina(1);
  }

  const campos =
    usuario.nivel === 1
      ? [
          {
            nome: 'empresa_id',
            rotulo: 'Empresa',
            tipo: 'select',
            opcoes: empresas.map((e) => ({ valor: e.public_id, rotulo: e.e1_nome })),
          },
          ...CAMPOS_BUSCA,
        ]
      : CAMPOS_BUSCA;

  function aoMudarPerPage(novoPerPage) {
    setPerPage(novoPerPage);
    setPagina(1);
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
        <h1 className="h4 mb-0">Clientes</h1>
        {usuario.permissoes?.clientes_criar === true && (
          <Button as={Link} to="/clientes/novo" variant="primary">
            Novo cliente
          </Button>
        )}
      </div>

      <BuscaAvancada
        campos={campos}
        placeholderSimples="Buscar por nome, e-mail, telefone ou CPF/CNPJ"
        aoBuscar={aoBuscar}
        initialQ={qInicial}
      />

      {!clientes && !erro && <Spinner animation="border" />}

      {clientes && (
        <>
          <Table hover responsive>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Telefone</th>
                <th>E-mail</th>
                <th>Categoria</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {clientes.map((cli) => (
                <tr key={cli.public_id}>
                  <td>{cli.c1_nome}</td>
                  <td>{cli.c1_celular || cli.c1_telefone}</td>
                  <td>{cli.c1_email}</td>
                  <td>{cli.c1_categoria}</td>
                  <td>
                    {cli.c1_bloqueio === 'true' ? (
                      <Badge bg="danger">Bloqueado</Badge>
                    ) : (
                      <Badge bg="success">Ativo</Badge>
                    )}
                  </td>
                  <td className="text-end">
                    <Button as={Link} to={`/clientes/${cli.public_id}`} size="sm" variant="outline-secondary">
                      {usuario.permissoes?.clientes_editar === true ? 'Editar' : 'Ver'}
                    </Button>
                  </td>
                </tr>
              ))}
              {clientes.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted">
                    {Object.keys(filtros).length > 0 ? 'Nenhum cliente encontrado com esses filtros.' : 'Nenhum cliente cadastrado ainda.'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <Paginacao meta={meta} aoMudarPagina={setPagina} aoMudarPerPage={aoMudarPerPage} />
        </>
      )}
    </>
  );
}
